import { AlertCircle, LifeBuoy, Receipt, TrendingDown, type LucideIcon } from 'lucide-react';
import { formatDate, formatHours, formatMoney, formatPercent } from '../../lib/format';
import { MARGIN_BANDS } from '../agreements/components';
import type { VerdictTone } from '../agreements/verdict';
import type { ClientDetail, ClientSupport, PortfolioRollup } from './api';
import { EXPIRY_HORIZON_DAYS } from './support-signal';
import { unbilledSplit, unbilledWizardHref } from './unbilled';

/**
 * THE client verdict — the same arbitration the agreement page does, one level up.
 *
 * A client page that lists four revenue-and-AR tiles cannot answer the only
 * question worth asking: *is this client worth having?* Two agreements at 25.7%
 * and 55.1% margin with $24,360 forty days past due is not "healthy" and it is
 * not "losing money" — it is a collection problem on profitable work, and
 * nothing on the page said so.
 *
 * Like `agreements/verdict.ts` this composes ONLY server-computed figures
 * (`stats` + `rollup`) into English. It re-derives no money.
 */

export interface ClientVerdict {
  tone: VerdictTone;
  label: string;
  reason: string;
}

export interface ClientAction {
  label: string;
  to: string;
  title: string;
  icon: LucideIcon;
}

const money = (v: number) => formatMoney(v, { whole: true });
const hours = (v: number) => formatHours(v, { unit: true });

/** "$24,360 of $48,680" style share, guarding a zero denominator. */
function shareOf(part: number, whole: number): string | null {
  if (!(whole > 0)) return null;
  return formatPercent(part / whole, { decimals: 0 });
}

/**
 * The support BANK, in the verdict's own words — the balance, priced.
 *
 * A client whose support hours run out in sixteen days was reading "Healthy"
 * here while `/support` flagged them LOW, because this file only ever looked at
 * invoices and margin. Support is money too, so it gets a clause in every
 * verdict and, when it is the worst thing about the client, a verdict of its
 * own.
 */
/**
 * Hours SOLD on an invoice that never reached the bank (a hand-built sale with
 * no hour block). When they exist, an overage is very likely that missing sale,
 * and every sentence about it names the invoice (Jesse, 2026-10-30: Compulink
 * read 3.5 h over while #26-0003 had sold 80 h — *"it's hard to tell that it's
 * related to the invoice"*).
 */
export function unbankedSummary(
  support: ClientSupport | null,
): { hours: number; numbers: string; invoices: string } | null {
  const sales = support?.unbankedSales ?? [];
  if (sales.length === 0) return null;
  const list = [...new Set(sales.map((s) => `#${s.invoiceNumber}`))];
  const numbers = list.join(', ');
  return {
    hours: sales.reduce((sum, s) => sum + s.hours, 0),
    numbers,
    invoices: `${list.length === 1 ? 'invoice' : 'invoices'} ${numbers}`,
  };
}

/**
 * The support clause, plus — when the bank holds hours from a sent invoice
 * nobody has paid — one clause saying so. Those hours count (they were
 * billed), so they are never called overage; the money is still owed.
 */
function supportClause(support: ClientSupport | null): string {
  const base = supportBankClause(support);
  const unpaid = support?.bank?.unpaidInvoices ?? [];
  const unpaidHours = unpaid.reduce((sum, u) => sum + u.hours, 0);
  if (!base || unpaidHours <= 0) return base;
  const numbers = unpaid.map((u) => `#${u.invoiceNumber}`).join(', ');
  return `${base} ${hours(unpaidHours)} of it ${unpaidHours === 1 ? 'is' : 'are'} on ${numbers}, not paid yet.`;
}

function supportBankClause(support: ClientSupport | null): string {
  const bank = support?.bank ?? null;
  if (!bank) return '';
  const unbanked = unbankedSummary(support);
  if (bank.overage > 0 && unbanked) {
    return ` Support reads ${hours(bank.overage)} beyond the bank, but ${unbanked.invoices} sold ${hours(unbanked.hours)} that never reached it.`;
  }
  // No `rate` is read here on purpose: every clause below states HOURS. The
  // only dollar figure this paragraph carries is `unbilledClause`'s, and it
  // prices delivered work, not a balance.
  if (bank.overage > 0) {
    // ⚠ THE PARAGRAPH PRICES THESE HOURS EXACTLY ONCE, AND NOT HERE.
    //
    // It used to read "Support is 17.00 h beyond the bank ($2,805 of work)"
    // and then, one sentence later, "$2,985 of delivered work has never been
    // invoiced" — two dollar values for one set of hours, in consecutive
    // sentences, as if they were two pots. They are not: $2,985 is that same
    // $2,805 of time plus $180 of billable expenses, and `unbilledClause`
    // now writes that addition out. `rate` is also the TOP-UP price (see
    // `support-signal.ts`), not the rate the invoice will use.
    return ` Support is ${hours(bank.overage)} beyond the bank with nothing left to draw on.`;
  }
  if (bank.status === 'exhausted') return ' The support bank is empty.';

  // ⚠ EXPIRY IS NOT PACE, AND THIS SENTENCE USED TO CONFLATE THEM.
  // `projectedExhaustion` is the EARLIER of the two, so when the calendar wins
  // the old wording printed the pace's name over the calendar's date:
  // "down to 27.75 h — about 23 days at 2.29 h/week" is arithmetic that does
  // not survive being read (2.29 h/week is 7.5 h in 23 days, not 27.75). Worse,
  // it implied the hours would be USED, when in fact they are about to be lost.
  // The expiry reading therefore leads with the whole balance and names the
  // pace date as the thing being given up.
  if (bank.exhaustionReason === 'expiry' && bank.projectedExhaustion) {
    // ⚠ NO PRICE ON THE BALANCE. This clause read "All 27.75 h ($4,163) in the
    // support bank expire…" — remaining hours × the block's rate, which is the
    // deferred-revenue figure Jesse ruled out ("just a FYI", not a decision
    // input). Unspent hours are hours; see `support-signal.ts`.
    const when =
      bank.daysToExhaustion !== null ? `, in ${bank.daysToExhaustion} days` : '';
    const pace = bank.paceExhaustion
      ? ` — at ${formatHours(bank.burnPerWeek)} h/week they would have lasted to ${formatDate(bank.paceExhaustion)}`
      : '';
    return ` All ${hours(bank.balance)} in the support bank expire ${formatDate(
      bank.projectedExhaustion,
    )}${when}${pace}.`;
  }

  if (bank.status === 'low') {
    const when = bank.projectedExhaustion
      ? ` — about ${bank.daysToExhaustion} days at ${formatHours(bank.burnPerWeek)} h/week, out on ${formatDate(bank.projectedExhaustion)}`
      : '';
    return ` Support hours are down to ${hours(bank.balance)}${when}.`;
  }
  return ` ${hours(bank.balance)} left in the support bank.`;
}

/**
 * Work delivered that nobody has been asked to pay for — in the verdict's own
 * words, in EVERY branch.
 *
 * ⚠ THIS IS A DIFFERENT FACT FROM OVERDUE AR AND ONE MUST NEVER SUPPRESS THE
 * OTHER. Unbilled is work done and not billed; overdue is work billed and not
 * paid. This page used to state only the second: the verdict sentence talked
 * about AR, the primary button chased it, and the four tiles were revenue /
 * cost / margin / AR — so on the six clients that had both, the unbilled figure
 * appeared nowhere at all. That was $37,272 of the company's $38,791 of
 * unbilled work, 96%, invisible on the pages it belongs to.
 *
 * The figure is `rollup.unbilled` — the one basis-aware, bank-aware definition
 * the server computes (fee-agreement hours are cost, prepaid support hours are
 * already paid for). It is never re-derived here.
 */
function unbilledClause(stats: ClientDetail['stats'], rollup: PortfolioRollup): string {
  if (rollup.unbilled <= 0) return '';
  const since = stats.unbilledFrom ? `, oldest ${formatDate(stats.unbilledFrom)}` : '';
  // The split, in the same sentence as the total — so a reader who has just
  // been told how many hours are beyond the support bank can see which half of
  // this figure prices them, and does not have to hold two numbers.
  const split = unbilledSplit(rollup.unbilled, rollup.unbilledExpenses);
  return ` ${money(rollup.unbilled)} of delivered work has never been invoiced${since}${
    split ? ` — ${split}` : ''
  }.`;
}

/**
 * Hours that will be GONE on a date the burn rate has no vote in, soon enough
 * to act on. It is a verdict in its own right: a bank can sit at 46% — a green
 * "In credit" by balance alone — and still lose every hour of it in three
 * weeks, which is precisely the contradiction a critic found on `/support`.
 */
function expiringSoon(bank: NonNullable<ClientSupport['bank']>): boolean {
  return (
    bank.exhaustionReason === 'expiry' &&
    bank.balance > 0 &&
    bank.daysToExhaustion !== null &&
    bank.daysToExhaustion <= EXPIRY_HORIZON_DAYS
  );
}

export function clientVerdict(
  stats: ClientDetail['stats'],
  rollup: PortfolioRollup,
  agreementCount: number,
  support: ClientSupport | null = null,
): ClientVerdict {
  const margin = rollup.client.margin;
  const revenue = rollup.client.revenue;
  const bank = support?.bank ?? null;
  const supportPart = supportClause(support);
  // Appended to every verdict that has a client to judge — see the note on
  // `unbilledClause`. It rides BESIDE the overdue sentence, never behind it.
  const unbilledPart = unbilledClause(stats, rollup);

  // --- nothing to judge ----------------------------------------------------
  if (agreementCount === 0 && stats.lifetimeRevenue === 0) {
    return {
      tone: 'neutral',
      label: 'No work yet',
      reason: 'No agreements and nothing invoiced — there is nothing to judge yet.',
    };
  }
  if (revenue === 0 && stats.openAr === 0) {
    return {
      tone: 'neutral',
      label: 'Not started',
      reason: `${agreementCount} agreement${agreementCount === 1 ? '' : 's'} with no recognised revenue yet — ${money(
        rollup.all.cost,
      )} of delivery cost so far.`,
    };
  }

  const overdueClause = (() => {
    if (stats.overdueAr <= 0) return '';
    const age = stats.oldestOverdueDays != null ? `, oldest ${stats.oldestOverdueDays} days past due` : '';
    return ` ${money(stats.overdueAr)} of the ${money(stats.openAr)} open AR is late${age}.`;
  })();

  // --- losing money --------------------------------------------------------
  if (margin != null && margin < 0) {
    return {
      tone: 'bad',
      label: 'Losing money',
      reason:
        `${money(rollup.client.cost)} of delivery cost against ${money(revenue)} of revenue across ` +
        `${rollup.client.agreements} agreement${rollup.client.agreements === 1 ? '' : 's'} — ` +
        `${money(rollup.client.cost - revenue)} under water.${overdueClause}${supportPart}${unbilledPart}`,
    };
  }

  // --- support worked beyond the bank --------------------------------------
  // Hours delivered with nothing paid for them are money being given away, and
  // until now this page called that client "Healthy".
  if (bank && bank.overage > 0 && stats.overdueAr <= 0) {
    const unbanked = unbankedSummary(support);
    return {
      tone: 'bad',
      label: 'Over the support bank',
      reason:
        (unbanked
          ? `${hours(bank.overage)} of support work reads beyond the ${hours(bank.purchased)} in the bank — ` +
            `but ${unbanked.invoices} sold ${hours(unbanked.hours)} that never reached it.`
          : `${hours(bank.overage)} of support work has been delivered beyond the ${hours(bank.purchased)} ` +
            `they bought, and the bank is empty.`) +
        (margin != null ? ` Delivery is ${formatPercent(margin, { decimals: 1 })} blended margin.` : '') +
        unbilledPart,
    };
  }

  // --- overdue -------------------------------------------------------------
  // Profitable work you have not been paid for is its own problem, and it is
  // the one with an obvious next step.
  if (stats.overdueAr > 0) {
    const share = shareOf(stats.overdueAr, stats.openAr);
    const marginPart =
      margin != null
        ? `Delivery is healthy at ${formatPercent(margin, { decimals: 1 })} blended margin, but `
        : '';
    const age =
      stats.oldestOverdueDays != null ? ` — oldest is ${stats.oldestOverdueDays} days past due` : '';
    const pace =
      stats.avgDaysToPay != null
        ? ` They normally pay ${Math.round(stats.avgDaysToPay)} days after issue.`
        : '';
    return {
      tone: stats.overdueAr >= stats.openAr / 2 ? 'bad' : 'risk',
      label: 'Overdue',
      reason:
        `${marginPart}${money(stats.overdueAr)}${share ? ` (${share} of open AR)` : ''} is past due across ` +
        `${stats.overdueCount} invoice${stats.overdueCount === 1 ? '' : 's'}${age}.${pace}${supportPart}${unbilledPart}`,
    };
  }

  // --- thin margin ---------------------------------------------------------
  if (margin != null && margin < MARGIN_BANDS.thin) {
    return {
      tone: 'watch',
      label: 'Thin margin',
      reason:
        `${formatPercent(margin, { decimals: 1 })} blended margin — ${money(rollup.client.cost)} of cost ` +
        `on ${money(revenue)} of revenue leaves little room. Nothing is overdue.${supportPart}${unbilledPart}`,
    };
  }

  // --- support running out -------------------------------------------------
  // Profitable, paid up, and out of support hours in a fortnight is not
  // "Healthy" — it is the call the account manager should make this week.
  if (bank && (bank.status === 'low' || bank.status === 'exhausted' || expiringSoon(bank))) {
    const marginPart =
      margin != null
        ? ` Delivery is fine at ${formatPercent(margin, { decimals: 1 })} blended margin, nothing overdue.`
        : '';
    return {
      tone: 'watch',
      label:
        bank.status === 'exhausted'
          ? 'Support bank empty'
          : expiringSoon(bank)
            ? // The expiry outranks the balance: "low" describes how much is
              // left, and the point is that ALL of it goes on one date.
              'Support hours expiring'
            : 'Support hours low',
      reason: `${supportClause(support).trim()}${marginPart}${unbilledPart}`,
    };
  }

  // --- dormant -------------------------------------------------------------
  // No active agreements is not health, it is silence. This client used to wear a
  // green HEALTHY pill on the strength of work that ended months ago.
  if (stats.activeAgreements === 0 && stats.totalAgreements > 0 && stats.openAr === 0) {
    const billed = stats.lastInvoiceDate ? `, last invoiced ${formatDate(stats.lastInvoiceDate)}` : '';
    return {
      tone: 'neutral',
      label: 'Dormant',
      reason:
        `No active work — all ${stats.totalAgreements} agreement${stats.totalAgreements === 1 ? '' : 's'} ` +
        `${stats.totalAgreements === 1 ? 'is' : 'are'} archived${billed}. ` +
        `${money(stats.lifetimeRevenue)} invoiced over the relationship` +
        (margin != null ? ` at ${formatPercent(margin, { decimals: 1 })} blended margin` : '') +
        `, nothing outstanding.` +
        unbilledPart,
    };
  }

  // --- healthy -------------------------------------------------------------
  const parts: string[] = [];
  if (margin != null) {
    parts.push(`${formatPercent(margin, { decimals: 1 })} blended margin on ${money(revenue)} of revenue`);
  }
  if (stats.openAr > 0) {
    parts.push(`${money(stats.openAr)} open AR, none of it late`);
  } else if (stats.avgDaysToPay != null) {
    parts.push(`nothing outstanding, paying in ~${Math.round(stats.avgDaysToPay)} days`);
  }
  return {
    tone: 'good',
    label: 'Healthy',
    reason: `${parts.join('; ')}.${supportPart}${unbilledPart}`,
  };
}

/**
 * What to do about money already BILLED and not paid. Its own function, because
 * it is its own fact — see `clientActions`.
 */
function chaseAction(clientId: string, stats: ClientDetail['stats']): ClientAction | null {
  if (stats.overdueAr <= 0) return null;
  return {
    label: `Chase ${money(stats.overdueAr)} overdue`,
    to: `/invoices?state=LATE&client=${encodeURIComponent(clientId)}`,
    icon: AlertCircle,
    title: `${stats.overdueCount} invoice${stats.overdueCount === 1 ? '' : 's'} past due${
      stats.oldestOverdueDays != null ? `, oldest by ${stats.oldestOverdueDays} days` : ''
    }`,
  };
}

/**
 * What to do about the DELIVERY side — support hours already spent (or about to
 * expire), work delivered and never invoiced, and the rates behind a thin
 * margin. Deliberately knows nothing about AR.
 *
 * Order within it is by size of lever: support hours already given away → money
 * earned but never invoiced → the rates that produced a thin margin.
 */
function deliveryAction(
  clientId: string,
  stats: ClientDetail['stats'],
  rollup: PortfolioRollup,
  support: ClientSupport | null,
): ClientAction | null {
  const bank = support?.bank ?? null;
  // An empty bank is the next most time-critical thing: every support hour
  // logged from here is work nobody has paid for. The link is this page's own
  // Support tab, where the balance and "Add hours" sit — one click, no hunt
  // through /support for the row.
  if (
    bank &&
    (bank.status === 'overage' ||
      bank.status === 'exhausted' ||
      bank.status === 'low' ||
      expiringSoon(bank))
  ) {
    // ⚠ A TOP-UP PRICE, AND ONLY EVER UNDER THE WORD "Top up". It reaches
    // the label only when the overage has ALREADY been invoiced (the branch
    // below returns first while there is unbilled money), so it can never sit
    // beside the invoice figure for the same hours — which is the exact
    // collision the marker on /clients was carrying.
    // ⚠ HOURS ALREADY SOLD COME FIRST. An invoice that sold hours which never
    // reached the bank makes the overage look like unpaid work; invoicing it
    // would bill the client twice. Bank the sale, then see what is left.
    const unbanked = unbankedSummary(support);
    if (bank.overage > 0 && unbanked) {
      return {
        label: `Add ${hours(unbanked.hours)} from ${unbanked.numbers} to the bank`,
        to: `/clients/${clientId}?tab=support`,
        icon: LifeBuoy,
        title:
          `${unbanked.invoices[0].toUpperCase()}${unbanked.invoices.slice(1)} sold ${hours(unbanked.hours)} of support that never reached the bank, ` +
          `which is why it reads ${hours(bank.overage)} over. Add them before invoicing anything — ` +
          'the overage may be hours they already paid for.',
      };
    }
    const priced =
      support?.rate && bank.overage > 0 ? ` · ${money(bank.overage * support.rate)} to replace` : '';
    // ⚠ AN OVERAGE THAT IS STILL UNBILLED IS AN INVOICE, NOT A TOP-UP.
    //
    // The app computed the exact figure — Meridian, 17 h beyond every purchased
    // block, itemised into 12 ledger rows summing to −17 — and offered no way
    // to turn it into money: the only invoice path was the wizard, and the
    // wizard offered the WRONG number (every uninvoiced support hour, prepaid
    // ones included). It offers the right one now, so the row that raises the
    // alarm can also settle it. `rollup.unbilled` is net of the bank, so on a
    // support-only client it IS the overage.
    //
    // A top-up is still one click away on the tab, and it stays the action when
    // the overage has already been invoiced (nothing left to bill).
    if (bank.overage > 0 && rollup.unbilled > 0) {
      // ⚠ The label may only say "overage" while the figure IS the overage.
      // `rollup.unbilled` also carries uninvoiced billable expenses, so when
      // there are any it is "unbilled" and the tooltip names the two parts —
      // one word covering two different kinds of money is how this screen and
      // the wizard started describing the same draft differently.
      const withExpenses = rollup.unbilledExpenses > 0;
      return {
        label: `Invoice ${money(rollup.unbilled)} ${withExpenses ? 'unbilled' : 'overage'}`,
        to: unbilledWizardHref(clientId, stats.unbilledFrom),
        icon: Receipt,
        title:
          `${hours(bank.overage)} of support has been delivered beyond every purchased block. ` +
          `${money(rollup.unbilled)} has never been invoiced — the prepaid hours are excluded, so no hour is billed twice` +
          (withExpenses
            ? `, and ${money(rollup.unbilledExpenses)} of billable expenses rides on the same draft. `
            : '. ') +
          'Or open the Support tab to sell them more hours instead.',
      };
    }
    // "Top up" is the wrong verb for hours that are about to EXPIRE — buying
    // more does not rescue the ones already paid for. The button says what the
    // trip is actually for; the tab it opens carries extend / add / send-link.
    const expiring = bank.overage <= 0 && expiringSoon(bank);
    return {
      label: expiring
        ? `Review support — ${hours(bank.balance)} expire in ${bank.daysToExhaustion} days`
        : bank.overage > 0
          ? `Top up support — ${hours(bank.overage)} over${priced}`
          : `Top up support — ${hours(bank.balance)} left`,
      to: `/clients/${clientId}?tab=support`,
      icon: LifeBuoy,
      title: expiring
        ? // Hours, not dollars — pricing a balance is the deferral figure the
          // owner ruled out (see `support-signal.ts`). The rate below prices a
          // SALE (hours to sell back), which is a different question.
          `${hours(bank.balance)} of paid-for hours expire on ${formatDate(bank.projectedExhaustion!)} — lost whether or not anyone burns them`
        : bank.overage > 0
          ? `${hours(bank.overage)} of support work has been delivered beyond every purchased block` +
            (support?.rate ? ` — ${money(bank.overage * support.rate)} to sell them back at ${formatMoney(support.rate)}/h` : '')
          : bank.projectedExhaustion
            ? `At ${formatHours(bank.burnPerWeek)} h/week the bank runs out on ${formatDate(bank.projectedExhaustion)}`
            : 'The support bank is empty — the next hour logged is unpaid work',
    };
  }
  // The generic "Invoice $X unbilled" rung is GONE (Jesse, 2026-08-22):
  // hourly invoices are raised by amount, never by gathering hours, so the
  // rung proposed an action this shop never takes. The support-OVERAGE rung
  // above is different — overage IS billed (or sold back) — and stays.
  const margin = rollup.client.margin;
  if (margin != null && margin < MARGIN_BANDS.thin) {
    return {
      label: 'Review rates',
      to: `/agreements?client=${encodeURIComponent(clientId)}&sort=margin&dir=asc`,
      icon: TrendingDown,
      title: 'Open this client’s agreements worst-margin first — bill and cost rates are on each agreement’s Team tab',
    };
  }
  return null;
}

/**
 * The things to do next — the primary one first, and **never only one of the
 * two independent facts this page holds**.
 *
 * ⚠ THIS FUNCTION USED TO RETURN THE FIRST MATCH AND STOP. Overdue AR was the
 * first branch, so on a client who both owed us money AND had work we had never
 * invoiced, the second one silently lost its button — and since the KPI strip
 * carried no unbilled tile either, the figure appeared nowhere on the screen.
 * Six clients were in exactly that state, holding $37,272 of the org's $38,791
 * of unbilled work.
 *
 * So AR and delivery are arbitrated separately and the page renders both: the
 * first is the primary button, the second an outline beside it. Order still
 * follows the verdict — money already owed leads — and a client with only one
 * problem still gets exactly one button.
 */
export function clientActions(
  clientId: string,
  stats: ClientDetail['stats'],
  rollup: PortfolioRollup,
  support: ClientSupport | null = null,
): ClientAction[] {
  return [
    chaseAction(clientId, stats),
    deliveryAction(clientId, stats, rollup, support),
  ].filter((a): a is ClientAction => a !== null);
}
